import { useEffect, useMemo } from 'react';
import { useAppContext } from '@/context/AppContext';
import useChromeLayer from '@/hooks/useChromeLayer';
import {
    applyModuleAttachments,
    attachModulesMap,
    attachmentsFor,
} from '@/theme/module-attach';

/**
 * Keeps snapped header / viewer modules flush against their anchors.
 * Renders nothing; does no work on sites without snapped groups.
 */
export default function ModuleAttachments() {
    const { theme } = useAppContext();
    const layer = useChromeLayer();
    const attachments = useMemo(
        () => attachmentsFor(attachModulesMap(theme), layer),
        [theme, layer],
    );

    useEffect(() => {
        if (!attachments.size) {
            applyModuleAttachments(document, attachments);
            return undefined;
        }

        let frame = 0;
        const schedule = () => {
            if (frame) return;
            frame = requestAnimationFrame(() => {
                frame = 0;
                applyModuleAttachments(document, attachments);
            });
        };
        schedule();

        // Anchors move when they are dragged, restyled, resized, or re-rendered.
        // Our own translate writes also land here; the second pass finds nothing to change.
        const mutations = new MutationObserver(schedule);
        mutations.observe(document.body, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ['style', 'class', 'data-header-module'],
        });
        const sizes = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
        document.querySelectorAll('[data-header-module]').forEach((el) => sizes?.observe(el));
        window.addEventListener('resize', schedule);
        document.fonts?.addEventListener?.('loadingdone', schedule);

        return () => {
            if (frame) cancelAnimationFrame(frame);
            mutations.disconnect();
            sizes?.disconnect();
            window.removeEventListener('resize', schedule);
            document.fonts?.removeEventListener?.('loadingdone', schedule);
        };
    }, [attachments]);

    return null;
}
